import { useState } from "react";
import { PeriodType } from "@/types/dashboard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Calendar, RotateCw, SlidersHorizontal, Plus, Save, X, RotateCcw,
  Check, Sparkles, Building2, Eye, ShieldCheck
} from "lucide-react";
import { LiveClockWidget } from "./WidgetComponents";

interface DashboardToolbarProps {
  period: PeriodType;
  onPeriodChange: (p: PeriodType) => void;
  customFrom: string;
  customTo: string;
  onCustomDatesChange: (from: string, to: string) => void;
  selectedBranch: string;
  onBranchChange: (bId: string) => void;
  branches: Array<{ id: string; name: string }>;
  isEditMode: boolean;
  onToggleEditMode: () => void;
  onSaveLayout: () => void;
  onCancelEdit: () => void;
  onResetLayout: () => void;
  onOpenWidgetLibrary: () => void;
  onRefresh: () => void;
  loading: boolean;
  saving: boolean;
  lastUpdated: string | null;
  hasUnsavedChanges: boolean;
}

export const DashboardToolbar = ({
  period,
  onPeriodChange,
  customFrom,
  customTo,
  onCustomDatesChange,
  selectedBranch,
  onBranchChange,
  branches,
  isEditMode,
  onToggleEditMode,
  onSaveLayout,
  onCancelEdit,
  onResetLayout,
  onOpenWidgetLibrary,
  onRefresh,
  loading,
  saving,
  lastUpdated,
  hasUnsavedChanges,
}: DashboardToolbarProps) => {
  const [showCustomPicker, setShowCustomPicker] = useState(period === "custom");

  const handlePeriodSelect = (val: PeriodType) => {
    onPeriodChange(val);
    setShowCustomPicker(val === "custom");
  };

  if (isEditMode) {
    return (
      <div className="sticky top-14 z-30 p-3 sm:p-4 rounded-xl border border-blue-200 bg-white shadow-md flex flex-col sm:flex-row items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-900">Customize Dashboard Layout</span>
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 font-medium">
                  Unsaved Changes
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 hidden sm:block">
              Drag widgets to rearrange, resize columns, or add new widgets from the library.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end w-full sm:w-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={onOpenWidgetLibrary}
            className="text-xs gap-1.5 h-8 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Widget</span>
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={onResetLayout}
            className="text-xs gap-1.5 h-8 text-slate-500 hover:text-slate-900"
            title="Restore default dashboard layout"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Reset Default</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={onCancelEdit}
            className="text-xs gap-1.5 h-8 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <X className="h-3.5 w-3.5" />
            <span>Cancel</span>
          </Button>

          <Button
            size="sm"
            onClick={onSaveLayout}
            disabled={saving}
            className="text-xs gap-1.5 h-8 bg-blue-600 text-white hover:bg-blue-700 shadow-2xs font-medium"
          >
            {saving ? <RotateCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            <span>Save Layout</span>
          </Button>
        </div>
      </div>
    );
  }

  // Normal View Mode Toolbar
  return (
    <div
      data-testid="dashboard-toolbar"
      className="flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-3.5 pb-2 border-b border-slate-200/80"
    >
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Period Selector */}
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-slate-400 hidden sm:block" />
          <Select value={period} onValueChange={handlePeriodSelect}>
            <SelectTrigger className="h-8 text-xs font-semibold w-[150px] sm:w-[170px] bg-white border-slate-200 text-slate-800">
              <SelectValue placeholder="Select Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="previous_month">Previous Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_fy">This Financial Year</SelectItem>
              <SelectItem value="custom">Custom Date Range</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Custom Date Range Picker */}
        {showCustomPicker && (
          <div className="flex items-center gap-1.5 animate-in fade-in">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => onCustomDatesChange(e.target.value, customTo)}
              className="h-8 text-xs w-[125px] bg-white border-slate-200 text-slate-800 px-2"
            />
            <span className="text-xs text-slate-400">to</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => onCustomDatesChange(customFrom, e.target.value)}
              className="h-8 text-xs w-[125px] bg-white border-slate-200 text-slate-800 px-2"
            />
          </div>
        )}

        {/* Branch Filter (if multiple branches) */}
        {branches.length > 1 && (
          <div className="flex items-center gap-1.5">
            <Building2 className="h-4 w-4 text-slate-400 hidden sm:block" />
            <Select value={selectedBranch} onValueChange={onBranchChange}>
              <SelectTrigger className="h-8 text-xs font-semibold w-[130px] sm:w-[150px] bg-white border-slate-200 text-slate-800">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Action buttons (Refresh & Customize) */}
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[11px] text-slate-500 hidden 2xl:inline font-medium">
              Updated {lastUpdated}
            </span>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={loading}
            className="h-8 text-xs gap-1.5 bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 px-2.5"
            title="Refresh real-time financial metrics"
          >
            <RotateCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={onToggleEditMode}
            className="h-8 text-xs gap-1.5 bg-white border-slate-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 px-3 font-semibold"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span>Customize</span>
          </Button>
        </div>
      </div>

      {/* Prominently aligned Live Clock widget */}
      <div className="w-full xl:w-auto shrink-0 flex justify-start xl:justify-end">
        <LiveClockWidget variant="topbar" />
      </div>
    </div>
  );
};
